import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildQueryFilters, useGetChildOrganizations } from '@sib/api';
import { OrganizationDto } from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Skeleton,
} from '@sib/ui';
import { ChevronDown, TeamOrg } from '@sib/ui/icons';
import { useUploadQueueGuard } from '@spendbrain/common/hooks';
import { paths } from '@spendbrain/config/paths';
import {
  OrgType,
  OrganizationSwitchDropdownItem,
  type OrganizationSwitchDropdownItemData,
} from './OrganizationSwitchDropdownItem';

export interface OrganizationSwitchButtonProps {
  organization: OrganizationDto | undefined;
  organizationId: string;
  setOrganizationId: (id: string, name: string) => void;
  setParentOrganization: (org: OrganizationDto | null) => void;
  entryPointOrganization: OrganizationDto;
  setEntryPointOrganization: (org: OrganizationDto | null) => void;
}

export function OrganizationSwitchButton({
  organization,
  organizationId,
  setOrganizationId,
  setParentOrganization,
  entryPointOrganization,
  setEntryPointOrganization,
}: OrganizationSwitchButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestAction } = useUploadQueueGuard();

  const { data: childData, isLoading } = useGetChildOrganizations(
    entryPointOrganization.id,
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const subsidiaryOrgs = useMemo<OrganizationSwitchDropdownItemData[]>(
    () =>
      childData?.items?.map(org => ({
        id: org.id,
        name: org.name,
        type: OrgType.Subsidiary,
      })) ?? [],
    [childData]
  );

  const parentOrg = useMemo<OrganizationSwitchDropdownItemData[]>(
    () => [
      {
        id: entryPointOrganization.id,
        name: entryPointOrganization.name,
        type: OrgType.Parent,
      },
    ],
    [entryPointOrganization]
  );

  const selectedOrg = useMemo<OrganizationSwitchDropdownItemData | null>(() => {
    if (!organization) return null;

    return {
      id: organization.id,
      name: organization.name,
      type:
        organization.id === entryPointOrganization.id
          ? OrgType.Parent
          : OrgType.Subsidiary,
    };
  }, [organization, entryPointOrganization]);

  const applyOrganizationChange = useCallback(
    (org: OrganizationSwitchDropdownItemData) => {
      setOrganizationId(org.id, org.name);
      const isSubsidiary = org.type === OrgType.Subsidiary;
      setParentOrganization(isSubsidiary ? (organization ?? null) : null);
      if (org.id === entryPointOrganization.id) {
        setEntryPointOrganization(null);
      }
      navigate(paths.dashboard);
    },
    [
      setOrganizationId,
      setParentOrganization,
      organization,
      entryPointOrganization,
      setEntryPointOrganization,
      navigate,
    ]
  );

  const handleSelect = useCallback(
    (org: OrganizationSwitchDropdownItemData) => {
      if (org.id === organizationId) return;

      requestAction(() => applyOrganizationChange(org));
    },
    [organizationId, requestAction, applyOrganizationChange]
  );

  if (isLoading || !organization) return <Skeleton className="h-8 w-30" />;

  const organizationSwitchShortLabel = {
    [OrgType.Parent]: t('components.organizationSwitchButton.parentShort'),
    [OrgType.Subsidiary]: t(
      'components.organizationSwitchButton.subsidiaryShort'
    ),
  } as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <TeamOrg className="text-primary h-5 w-5" />
          <span className="hidden sm:inline">{selectedOrg?.name}</span>
          <span className="sm:hidden">
            {selectedOrg ? organizationSwitchShortLabel[selectedOrg.type] : ''}
          </span>
          <ChevronDown className="text-muted-foreground ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-1">
        <DropdownMenuLabel className="text-muted-foreground px-2 pb-0 text-xs font-normal">
          {t('components.organizationSwitchButton.parent')}
        </DropdownMenuLabel>
        {parentOrg.map(org => (
          <OrganizationSwitchDropdownItem
            key={org.id}
            org={org}
            isSelected={selectedOrg?.id === org.id}
            onSelect={handleSelect}
          />
        ))}
        {subsidiaryOrgs.map(org => (
          <OrganizationSwitchDropdownItem
            key={org.id}
            org={org}
            isSelected={selectedOrg?.id === org.id}
            onSelect={handleSelect}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
